import { useMemo, useRef, useState } from "react";
import { Plus, ChevronDown } from "lucide-react";
import {
  CONDITION_CATEGORIES,
  CATEGORY_BY_ID,
  ACCENT_CLASS,
  categoryOfCondition,
} from "@/constants/conditionCategories";
import { createDefaultCondition, uid } from "./segmentFormUtils";
import {
  MEMBERSHIP_CATEGORIES,
  membershipFields,
  tagOpForCategory,
  makeTagCondition,
} from "./conditionModel";
import CategoryCard from "./CategoryCard";

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-(--radius-md) bg-(--surface-2) p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`rounded-[calc(var(--radius-md)-2px)] px-3 py-1.5 text-sm font-medium transition-colors ${
            value === opt.value
              ? "bg-(--surface-1) text-(--text-primary) shadow-sm"
              : "text-(--text-secondary) hover:text-(--text-primary)"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function AddGroupMenu({ available, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  if (available.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-3 py-2.5 text-sm font-medium text-(--brand-primary) hover:bg-(--surface-2)"
      >
        <Plus size={14} /> Add condition group <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute bottom-full left-1/2 z-10 mb-1 w-56 -translate-x-1/2 overflow-hidden rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) py-1 shadow-lg">
          {available.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(category.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-(--text-primary) hover:bg-(--surface-2)"
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-(--radius-sm) ${
                    ACCENT_CLASS[category.accent] ?? ACCENT_CLASS.slate
                  }`}
                >
                  <Icon size={13} strokeWidth={2} />
                </span>
                {category.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const connectorLabel = (activeTab, combinator) =>
  activeTab === "exclude" ? "OR" : combinator === "and" ? "AND" : "OR";

export default function BuildConditions({
  definition,
  onSetGroup,
  fieldOptionConfig,
}) {
  const [activeTab, setActiveTab] = useState("include");
  const [combinator, setCombinator] = useState(() =>
    (definition.any?.length ?? 0) > 0 && (definition.all?.length ?? 0) === 0
      ? "or"
      : "and",
  );

  const includeGroup = combinator === "and" ? "all" : "any";
  const group = activeTab === "exclude" ? "none" : includeGroup;
  const conditions = definition[group] ?? [];

  const { byCategory, orderedIds } = useMemo(() => {
    const map = {};
    const order = [];
    for (const cond of conditions) {
      const cat = categoryOfCondition(cond);
      if (!map[cat]) {
        map[cat] = [];
        order.push(cat);
      }
      map[cat].push(cond);
    }
    return { byCategory: map, orderedIds: order };
  }, [conditions]);

  const presentCategories = orderedIds
    .map((id) => CATEGORY_BY_ID[id])
    .filter(Boolean);
  const availableCategories = CONDITION_CATEGORIES.filter(
    (c) => !byCategory[c.id],
  );

  const handleCombinatorChange = (next) => {
    if (next === combinator) return;
    const from = combinator === "and" ? "all" : "any";
    const to = next === "and" ? "all" : "any";
    const moved = (definition[from] ?? []).map((cond) =>
      cond.field === "tags"
        ? {
            ...cond,
            op: tagOpForCategory(categoryOfCondition(cond), next, "include"),
          }
        : cond,
    );
    onSetGroup(from, []);
    onSetGroup(to, [...(definition[to] ?? []), ...moved]);
    setCombinator(next);
  };

  const setCategoryConditions = (categoryId, nextForCategory) => {
    const result = [];
    let inserted = false;
    for (const c of conditions) {
      if (categoryOfCondition(c) === categoryId) {
        if (!inserted) {
          result.push(...nextForCategory);
          inserted = true;
        }
      } else {
        result.push(c);
      }
    }
    if (!inserted) result.push(...nextForCategory);
    onSetGroup(group, result);
  };

  const addCategory = (categoryId) => {
    const category = CATEGORY_BY_ID[categoryId];
    let seed;
    if (category.kind === "tags") {
      seed = [
        makeTagCondition(
          categoryId,
          tagOpForCategory(categoryId, combinator, activeTab),
        ),
      ];
    } else if (MEMBERSHIP_CATEGORIES.has(categoryId)) {
      const first = membershipFields(categoryId, fieldOptionConfig)[0];
      seed = [
        {
          id: uid(),
          field: first.field,
          op: first.op,
          value: [],
          disabled: false,
        },
      ];
    } else if (categoryId === "behavior") {
      seed = [
        {
          id: uid(),
          field: "sessionDuration",
          op: "gte",
          value: "",
          disabled: false,
        },
      ];
    } else {
      seed = [createDefaultCondition(category.fields[0], fieldOptionConfig)];
    }
    onSetGroup(group, [...conditions, ...seed]);
  };

  const removeCategory = (categoryId) => {
    onSetGroup(
      group,
      conditions.filter((c) => categoryOfCondition(c) !== categoryId),
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-(--text-primary)">
            Build conditions
          </h3>
          <p className="mt-0.5 text-sm text-(--text-secondary)">
            {activeTab === "exclude"
              ? "Exclude viewers who match any of these conditions."
              : "Include viewers that match the following conditions."}
          </p>
        </div>
        <Segmented
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: "include", label: "Include" },
            { value: "exclude", label: "Exclude" },
          ]}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {activeTab === "include" ? (
          <Segmented
            value={combinator}
            onChange={handleCombinatorChange}
            options={[
              { value: "and", label: "Match ALL (AND)" },
              { value: "or", label: "Match ANY (OR)" },
            ]}
          />
        ) : (
          <span className="text-xs text-(--text-muted)">
            A viewer is excluded if any condition below matches.
          </span>
        )}
      </div>

      {presentCategories.length === 0 ? (
        <div className="rounded-(--radius-lg) border border-dashed border-(--border-subtle) px-4 py-10 text-center">
          <p className="text-sm text-(--text-secondary)">
            No conditions yet. Add a condition group to start targeting.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {presentCategories.map((category, index) => (
            <div key={category.id} className="relative">
              {index > 0 && (
                <span className="absolute -top-1.5 left-4 z-10 -translate-y-1/2 rounded-(--radius-pill) bg-(--brand-primary) px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-(--brand-primary-contrast) shadow-sm ring-4 ring-(--surface-1)">
                  {connectorLabel(activeTab, combinator)}
                </span>
              )}
              <CategoryCard
                category={category}
                conditions={byCategory[category.id]}
                combinator={combinator}
                activeTab={activeTab}
                fieldOptionConfig={fieldOptionConfig}
                onSetConditions={(next) =>
                  setCategoryConditions(category.id, next)
                }
                onRemoveCategory={() => removeCategory(category.id)}
              />
            </div>
          ))}
        </div>
      )}

      <AddGroupMenu available={availableCategories} onPick={addCategory} />
    </div>
  );
}
